"use client";
import {
  EventNamesEnum,
  langDetails,
  LangEnum,
  RUDDERSTACK_ENDPOINT,
} from "@/constants/constants";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { captureError, isTvEnv } from "@/utils/common.helpers";
import { getDevicePlatform } from "@/utils/userAgent.helpers";
import { createContext, useContext, useEffect } from "react";
import { getUAParser } from "@/utils/lazyUAParser";
import { getOrCreateDeviceIdClient } from "@/utils/deviceId";

// Create a context for RudderStack analytics
const RudderAnalyticsContext = createContext(
  typeof window !== "undefined" && window?.rudderanalytics
    ? window.rudderanalytics
    : null,
);

// Provider component that loads and provides rudderAnalytics
export const InitializeRudderStack = ({}) => {
  let isLoadingRudderAnalytics = false;
  const { trackEvent } = useTrackingAnalytics();
  useEffect(() => {
    (async () => {
      // this window.isRudderAnalyticsInitialized is used to check if the rudder analytics is initialized
      if (!isLoadingRudderAnalytics && !window?.isRudderAnalyticsInitialized) {
        isLoadingRudderAnalytics = true;
        try {
          const { RudderAnalytics } = await (isTvEnv()
            ? import(
                /* webpackPreload: true */
                "@rudderstack/analytics-js/bundled"
              )
            : import(
                /* webpackPreload: true */
                "@rudderstack/analytics-js"
              ));

          const rudderanalytics = new RudderAnalytics();

          window.rudderanalytics = rudderanalytics;
          window.isRudderAnalyticsInitialized = true;

          // Get deviceId to use as anonymousId for all events
          const deviceId = await getOrCreateDeviceIdClient();

          rudderanalytics.load(
            process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY ||
              "36sYTD5Z5Pr03SV45SXfSY26ASP", // default dev key
            process.env.NEXT_PUBLIC_RUDDERSTACK_ENDPOINT ||
              RUDDERSTACK_ENDPOINT,
            {
              integrations: { All: true },
            },
          );

          const trackAppOpen = async () => {
            const parser = await getUAParser();

            const deviceProperties = {
              session_id: rudderanalytics.getSessionId(),
              app_display_language:
                langDetails[
                  (document.documentElement.lang ||
                    process.env.NEXT_PUBLIC_DEFAULT_LANG) as LangEnum
                ]?.apiKey,
              device_platform: getDevicePlatform(),
              device_os_name: parser.getOS().name,
              device_os_version: parser.getOS().version,
              device_os_language: navigator.language,
              device_browser_name: parser.getBrowser().name,
              device_browser_version: parser.getBrowser().version,
            };
            trackEvent({
              eventName: EventNamesEnum.APP_OPEN,
              properties: deviceProperties,
            });
          };
          rudderanalytics.ready(() => {
            // Set anonymousId to deviceId so all events use the same identifier
            // This ensures user journey continuity in Amplitude
            rudderanalytics.setAnonymousId(deviceId);
            trackAppOpen();
          });
        } catch (e) {
          console.error("RudderStack initialization failed", e);
        }
      }
    })();
  }, []);

  return null;
};

// Custom hook to use rudderAnalytics
export const useRudderAnalytics = () => {
  const context = useContext(RudderAnalyticsContext);
  if (context === undefined) {
    throw captureError(
      new Error(
        "useRudderAnalytics must be used within a RudderAnalyticsProvider",
      ),
    );
  }
  return context;
};
